import { isEmpty, uuidv4 } from "src/shared/utils";
import { LOCALSTORAGE_TOKEN_KEY, LOCALSTORAGE_USER_META } from "../config";
import { ICustomError, IRequestData } from "./types";

class ApiUtils {
  protected addDefaultValues(
    requestData: IRequestData,
    url: string,
    baseUrl?: string,
    defaultHeaders?: Record<string, string>,
    defaultConfig?: RequestInit
  ): void {
    requestData.url = `${baseUrl}${url}`;
    /* Not added x-token in default header because it will not update after each request call
     * and will save and send token value which was at the start of webapp
     */

    requestData.headers = {
      "x-token": String(localStorage.getItem(LOCALSTORAGE_TOKEN_KEY)),
      "x-app-meta": String(localStorage.getItem(LOCALSTORAGE_USER_META)),
      "X-Request-ID": uuidv4(),
      ...defaultHeaders,
      ...requestData.headers,
    };
    requestData.config = { ...defaultConfig, ...requestData.config };
  }

  protected formatErrorMessage = (
    body = null,
    status: null | number | undefined = null,
    errorMessage = "Something went wrong, please try again later"
  ): { response; error: ICustomError } => {
    const isUserOnline = navigator.onLine;

    return {
      error: {
        response: body,
        message: errorMessage,
        status: status || (isUserOnline ? 503 : null),
        type: isUserOnline ? "server" : "network",
      },
      response: null,
    };
  };

  protected hasJson(headers): boolean {
    const contentType = headers.get("Content-Type");
    return contentType !== null ? contentType.includes("application/json") : false;
  }

  protected formatRequestData = (data: any) => {
    if (data instanceof FormData) return data;
    if (typeof data === "string") return data;
    return JSON.stringify(data);
  };

  protected async parseResponse(response: Response) {
    const data = await response.text();
    if (this.hasJson(response.headers) && data.length !== 0) {
      try {
        return JSON.parse(data);
      } catch (error) {
        // If JSON parsing fails, return the original text with error info
        return {
          error: true,
          message: data,
          originalError: error instanceof Error ? error.message : "Unknown parsing error",
        };
      }
    }
    return data;
  }

  static buildUrlWithParams = (url: string, params: Record<string, string> = {}): string => {
    if (!isEmpty(params)) {
      return `${url}?${new URLSearchParams({ ...params })}`;
    }
    return url;
  };
}

export default ApiUtils;
